import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Lock, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const authError = useLocation().state?.authError;

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen bg-[#FBF6F4] lg:grid-cols-2">
      <div className="relative flex flex-col justify-between px-7 py-10 sm:px-14 lg:px-20 lg:py-14">
        <div className="flex items-center gap-3">
          <img
            src="/chambers-logo.png"
            alt="Chambers of Amir Arsiwala"
            className="h-11 w-11 object-contain"
            data-testid="login-logo"
          />
          <div>
            <p className="font-serif text-xl leading-none text-slate-900">Chambers&rsquo; Billing</p>
            <p className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500">
              Chambers of Amir Arsiwala
            </p>
          </div>
        </div>

        <div className="max-w-md py-14 rise">
          <p className="label-caps">Litigation billing &amp; invoicing</p>
          <h1 className="mt-5 font-serif text-4xl leading-[1.08] tracking-tight text-slate-900 sm:text-5xl">
            Chambers of Amir Arsiwala
          </h1>
          <p className="mt-6 text-sm leading-relaxed text-slate-600">
            Billing and Invoicing Management Platform, for Chambers of Amir Arsiwala.
          </p>

          {authError ? (
            <div
              className="mt-9 flex items-start gap-3 border border-amber-300 bg-amber-50 px-4 py-3"
              data-testid="login-access-denied"
            >
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" strokeWidth={1.8} />
              <p className="text-[12px] leading-relaxed text-amber-900">{authError}</p>
            </div>
          ) : null}

          <button
            type="button"
            onClick={handleLogin}
            data-testid="google-login-button"
            className="group mt-10 inline-flex w-full items-center justify-between gap-4 bg-slate-900 px-6 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-slate-800 sm:w-auto"
          >
            Login with Google
            <ArrowUpRight
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={1.8}
            />
          </button>

          <p className="mt-5 flex items-center gap-2 text-[11px] text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.8} />
            Encrypted in transit. Privileged matter data stays in these chambers only.
          </p>
        </div>

        <p className="text-[11px] uppercase tracking-[0.16em] text-slate-400">
          Chambers of record · Fee notes · Receivables
        </p>
      </div>

      <div className="relative hidden overflow-hidden bg-slate-900 lg:flex lg:flex-col lg:justify-between">
        <div className="pointer-events-none absolute inset-0 opacity-[0.14]">
          <div className="absolute -right-24 top-1/2 h-[38rem] w-[38rem] -translate-y-1/2 rounded-full bg-brand-rose blur-[120px]" />
          <div className="absolute -left-20 bottom-0 h-72 w-72 rounded-full bg-brand-terracotta blur-[110px]" />
        </div>
        <div className="relative flex flex-1 items-center justify-center px-16">
          <img
            src="/chambers-logo.png"
            alt="Chambers crest"
            className="w-[62%] max-w-md object-contain drop-shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
          />
        </div>
        <div className="relative border-t border-white/15 bg-black/20 px-12 py-8 backdrop-blur-sm">
          <p className="font-serif text-lg text-white">
            &ldquo;The fee note should read as carefully as the pleading.&rdquo;
          </p>
          <p className="mt-2 text-[11px] uppercase tracking-[0.16em] text-white/60">
            Chambers of Amir Arsiwala
          </p>
        </div>
      </div>
    </div>
  );
}
